import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
// B-2026-0078: onboarding checklist
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { useOnboardingStatus } from '@/hooks/useOnboarding';
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts';
import {
  ArrowTrendingUpIcon,
  MinusCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon,
  UserGroupIcon,
  MegaphoneIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui';
import { PBDeltaCard } from '@/components/dashboard/PBDeltaCard';
import { RecurringIssuesCard } from '@/components/dashboard/RecurringIssuesCard';
import { RecentCoachCommentsCard } from '@/components/dashboard/RecentCoachCommentsCard';
import { VelocityDisplay } from '@/components/dashboard/VelocityCard';
import { velocityLabel } from '@/components/dashboard/velocityLabel';
import { MilestoneGrid } from '@/components/dashboard/MilestoneGrid';
import { useMyMilestones } from '@/hooks/useMilestones';
import { ChartTooltip } from '@/components/dashboard/ChartTooltip';
import { ScoreRing } from '@/components/ui/ScoreRing';
import { useMyEvaluations, useEvaluationDetails } from '@/hooks';
import { useMyChannels } from '@/hooks/useChannels';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate, scoreLabel } from '@/utils/formatters';
import { getMyChannelFeed } from '@/services/channelPostsApi';
import type { ChannelFeedPost } from '@/services/channelPostsApi';
import { toggleLeaderboardOptIn } from '@/services/leaderboardApi';
import { getChannelMembers } from '@/services/channelApi';
import type { RegionScore, PhaseScore, LLMExplanation } from '@/types/api';
import type { EvaluationSummaryItem } from '@/services/evaluationApi';
// Goal Score follow-ups batch 1 (frontend)
import type { PublishState } from '@/types/goalScore';
// Goal Score go-live B1 — athlete-owned leaderboard publish consent
import { setPublishConsent, extractPublishConsentErrorMessage } from '@/services/goalScoreApi';

// ─── helpers ────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 80) return 'var(--color-accent)';
  if (score >= 60) return 'var(--color-accent-gold)';
  return 'var(--color-accent-red)';
}

function scoreBand(score: number): 'green' | 'amber' | 'red' {
  if (score >= 80) return 'green';
  if (score >= 60) return 'amber';
  return 'red';
}

// slope and velocityLabel extracted to @/components/dashboard/VelocityCard.tsx (Feature 5)


// ─── Action Card ─────────────────────────────────────────────────────────────

interface ActionCardProps {
  title: string;
  count: number;
  subtitle: string;
  names: string[];
  accent: string;
  icon: React.ReactNode;
}

function ActionCard({ title, count, subtitle, names, accent, icon }: ActionCardProps) {
  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-card)',
        border: '1px solid var(--color-border-subtle)',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 'var(--radius-card)',
        padding: '20px 20px 20px 22px',
        boxShadow: 'var(--shadow-card)',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        flex: '1 1 0',
        minWidth: 0,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.28)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.transform = '';
        (e.currentTarget as HTMLDivElement).style.boxShadow = 'var(--shadow-card)';
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}
      >
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: `${accent}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: accent,
          }}
        >
          {icon}
        </div>
        <span
          style={{
            fontFamily: 'var(--font-heading)',
            fontWeight: 700,
            fontSize: '32px',
            color: accent,
            lineHeight: 1,
          }}
        >
          {count}
        </span>
      </div>
      <p
        style={{
          fontFamily: 'var(--font-heading)',
          fontWeight: 600,
          fontSize: '13px',
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          marginBottom: '4px',
        }}
      >
        {title}
      </p>
      <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '10px' }}>
        {subtitle}
      </p>
      {names.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {names.slice(0, 3).map((name) => (
            <li key={name} style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
              · {name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Phase Heatmap ───────────────────────────────────────────────────────────

interface HeatmapProps {
  evaluations: EvaluationSummaryItem[];
}

function PhaseHeatmap({ evaluations }: HeatmapProps) {
  const recent = evaluations.slice(-8);

  // Collect all phase names from the most recent evaluation that has phase data
  const phaseNames = useMemo(() => {
    const last = [...recent]
      .reverse()
      .find((e) => e.phase_scores && e.phase_scores.length > 0);
    if (!last?.phase_scores) return [];
    return last.phase_scores.map(
      (p) => p.phase_name ?? p.phase_label ?? `Phase ${p.phase_index ?? ''}`
    );
  }, [recent]);

  if (phaseNames.length === 0) {
    return (
      <p
        style={{
          fontSize: '13px',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
          padding: '20px 0',
        }}
      >
        No phase data available yet.
      </p>
    );
  }

  const cellBg = (score: number | undefined) => {
    if (score === undefined || score === null) return 'var(--color-dark-elevated-2)';
    if (score >= 80) return 'rgba(30, 215, 96, 0.22)';
    if (score >= 60) return 'rgba(244, 201, 93, 0.22)';
    return 'rgba(255, 93, 93, 0.22)';
  };
  const cellBorder = (score: number | undefined) => {
    if (score === undefined || score === null) return 'var(--color-border-subtle)';
    if (score >= 80) return 'rgba(30, 215, 96, 0.35)';
    if (score >= 60) return 'rgba(244, 201, 93, 0.35)';
    return 'rgba(255, 93, 93, 0.35)';
  };
  const cellText = (score: number | undefined) => {
    if (score === undefined || score === null) return 'var(--color-text-muted)';
    if (score >= 80) return 'var(--color-accent)';
    if (score >= 60) return 'var(--color-accent-gold)';
    return 'var(--color-accent-red)';
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: '4px',
          width: '100%',
          minWidth: '400px',
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'left',
                fontSize: '11px',
                color: 'var(--color-text-muted)',
                fontWeight: 500,
                paddingBottom: '6px',
                paddingRight: '12px',
                width: '140px',
              }}
            >
              Phase
            </th>
            {recent.map((e, i) => (
              <th
                key={e.evaluation_id}
                style={{
                  textAlign: 'center',
                  fontSize: '10px',
                  color: 'var(--color-text-muted)',
                  fontWeight: 400,
                  paddingBottom: '6px',
                  minWidth: '42px',
                }}
              >
                {i === recent.length - 1 ? 'Latest' : formatDate(e.created_at, 'M/d')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {phaseNames.map((phaseName) => (
            <tr key={phaseName}>
              <td
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-secondary)',
                  paddingRight: '12px',
                  paddingTop: '2px',
                  paddingBottom: '2px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '140px',
                }}
                title={phaseName}
              >
                {phaseName}
              </td>
              {recent.map((e) => {
                const ps = e.phase_scores?.find(
                  (p) =>
                    (p.phase_name ?? p.phase_label ?? `Phase ${p.phase_index ?? ''}`) ===
                    phaseName
                );
                const score =
                  ps?.form_score ?? ps?.score ?? ps?.overall_phase_score;
                return (
                  <td
                    key={e.evaluation_id}
                    title={score !== undefined ? `${Math.round(score)}` : 'No data'}
                    style={{
                      backgroundColor: cellBg(score),
                      border: `1px solid ${cellBorder(score)}`,
                      borderRadius: '6px',
                      textAlign: 'center',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: cellText(score),
                      padding: '6px 4px',
                    }}
                  >
                    {score !== undefined ? Math.round(score) : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Coaching History Accordion ───────────────────────────────────────────────

interface CoachingEntryProps {
  date: string | undefined;
  score: number;
  summary: string | undefined;
  tips: string[] | undefined;
  defaultOpen?: boolean;
  createdByDisplayName?: string | null;
  /** B-2026-0020: display name of the coach this evaluation was shared with */
  sharedWithDisplayName?: string | null;
  /**
   * Goal Score follow-ups batch 1: `null`/absent on every legacy evaluation
   * and whenever the Goal Score switches are off (today's prod default) —
   * in that case the whole Total/bonus line is omitted, never rendered as
   * "Total 0" (LFT-063 sentinel convention).
   */
  totalScore?: number | null;
  goalBonusScore?: number | null;
  publishState?: PublishState | null;
}

function CoachingEntry({
  date,
  score,
  summary,
  tips,
  defaultOpen = false,
  createdByDisplayName,
  sharedWithDisplayName,
  totalScore,
  goalBonusScore,
  publishState,
}: CoachingEntryProps) {
  const [open, setOpen] = useState(defaultOpen);
  const band = scoreBand(score);
  const color =
    band === 'green'
      ? 'var(--color-accent)'
      : band === 'amber'
      ? 'var(--color-accent-gold)'
      : 'var(--color-accent-red)';

  return (
    <div
      style={{
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: 'var(--color-dark-elevated-1)',
      }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
        aria-expanded={open}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              backgroundColor: `${color}18`,
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '15px',
              color,
              flexShrink: 0,
            }}
          >
            {Math.round(score)}
          </span>
          <div>
            <p
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                margin: 0,
              }}
            >
              Session — {formatDate(date)}
            </p>
            <p
              style={{
                fontSize: '11px',
                color: 'var(--color-text-muted)',
                margin: '2px 0 0',
              }}
            >
              Score: {Math.round(score)} / 100
            </p>
            {/* Goal Score follow-ups batch 1: Total + bonus, only when this
                evaluation actually measured a Goal (totalScore non-null). */}
            {totalScore != null && (
              <p
                data-testid="coaching-entry-total-score"
                style={{
                  fontSize: '11px',
                  color: 'var(--color-text-muted)',
                  margin: '2px 0 0',
                }}
              >
                Total: {Math.round(totalScore * 10) / 10}
                {goalBonusScore != null && ` (+${Math.round(goalBonusScore * 10) / 10} from Goals)`}
              </p>
            )}
            {/* Goal Score follow-ups batch 1: published/hidden marker
                (publish_state null means "not applicable" — no marker). */}
            {publishState && publishState !== 'private' && (
              <span
                data-testid="coaching-entry-publish-marker"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginTop: '4px',
                  fontSize: '10px',
                  fontWeight: 600,
                  color:
                    publishState === 'published'
                      ? 'var(--color-accent)'
                      : 'var(--color-text-muted)',
                  backgroundColor:
                    publishState === 'published'
                      ? 'rgba(30, 215, 96, 0.10)'
                      : 'rgba(135, 147, 163, 0.10)',
                  border:
                    publishState === 'published'
                      ? '1px solid rgba(30, 215, 96, 0.22)'
                      : '1px solid var(--color-border-subtle)',
                  borderRadius: '999px',
                  padding: '2px 8px',
                  textTransform: 'none',
                }}
              >
                {publishState === 'published' ? 'Published to leaderboard' : 'Hidden from leaderboard'}
              </span>
            )}
            {createdByDisplayName && (
              <p
                style={{
                  fontSize: '11px',
                  color: 'var(--color-text-muted)',
                  margin: '2px 0 0',
                }}
              >
                Evaluated by {createdByDisplayName}
              </p>
            )}
            {/* B-2026-0020: "Shared with [Coach]" badge */}
            {sharedWithDisplayName && (
              <span
                data-testid="shared-with-badge"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginTop: '4px',
                  fontSize: '10px',
                  fontWeight: 600,
                  color: 'var(--color-accent-blue)',
                  backgroundColor: 'rgba(61, 169, 252, 0.10)',
                  border: '1px solid rgba(61, 169, 252, 0.22)',
                  borderRadius: '999px',
                  padding: '2px 8px',
                  textTransform: 'none',
                }}
              >
                Shared with {sharedWithDisplayName}
              </span>
            )}
          </div>
        </div>
        <div style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
          {open ? (
            <ChevronUpIcon style={{ width: '16px', height: '16px' }} />
          ) : (
            <ChevronDownIcon style={{ width: '16px', height: '16px' }} />
          )}
        </div>
      </button>

      {open && (
        <div style={{ padding: '0 18px 18px' }}>
          {summary && (
            <p
              style={{
                fontSize: '13px',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.65,
                marginBottom: '14px',
              }}
            >
              {summary}
            </p>
          )}
          {tips && tips.length > 0 && (
            <>
              <p
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--color-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '8px',
                }}
              >
                Improvement Tips
              </p>
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                {tips.map((tip, i) => (
                  <li
                    key={i}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 1.5,
                    }}
                  >
                    <span
                      style={{
                        color: 'var(--color-accent)',
                        flexShrink: 0,
                        marginTop: '1px',
                      }}
                    >
                      •
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </>
          )}
          {!summary && (!tips || tips.length === 0) && (
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
              No coaching notes for this session.
            </p>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ position: 'relative', marginBottom: '28px' }}>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: '-20px',
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(30, 215, 96, 0.10) 0%, transparent 70%)',
          }}
        />
        <div
          style={{
            width: '72px',
            height: '72px',
            borderRadius: '20px',
            backgroundColor: 'rgba(30, 215, 96, 0.10)',
            border: '1px solid rgba(30, 215, 96, 0.20)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <SparklesIcon
            style={{ width: '32px', height: '32px', color: 'var(--color-accent)' }}
          />
        </div>
      </div>
      <h2
        style={{
          fontFamily: 'var(--font-heading)',
          fontWeight: 700,
          fontSize: '20px',
          color: 'var(--color-text-primary)',
          marginBottom: '10px',
        }}
      >
        Your progress journey starts here
      </h2>
      <p
        style={{
          fontSize: '14px',
          color: 'var(--color-text-muted)',
          maxWidth: '340px',
          lineHeight: 1.6,
          marginBottom: '24px',
        }}
      >
        Complete your first evaluation to unlock your Progress Pulse — a live view of your
        scores, trends, and AI coaching feedback.
      </p>
      {/* Primary CTA — routes to template selection so the athlete starts their first evaluation */}
      <Link
        to="/evaluate"
        data-testid="empty-state-run-first-eval-cta"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--color-text-inverse)',
          background: 'var(--color-accent)',
          borderRadius: '8px',
          padding: '10px 20px',
          textDecoration: 'none',
          boxShadow: '0 0 18px rgba(30, 215, 96, 0.25)',
        }}
      >
        Run your first evaluation
      </Link>
    </div>
  );
}

// BestVsLatestCard extracted to PBDeltaCard (Feature 5 / Phase D)

// (function body moved to PBDeltaCard.tsx — see import above)

// ─── My Channels Card ─────────────────────────────────────────────────────────

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const ms = Date.now() - date.getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function MyChannelsCard() {
  const { data: channels = [], isLoading } = useMyChannels();
  const { data: allEvaluations = [] } = useMyEvaluations();

  // Tier A2: compute last activity (most recent eval timestamp) per channel
  const lastActivityByChannel = useMemo(() => {
    const map = new Map<string, string>();
    for (const evalRow of allEvaluations) {
      if (!evalRow.channel_id) continue;
      const existing = map.get(evalRow.channel_id);
      if (!existing || evalRow.created_at > existing) {
        map.set(evalRow.channel_id, evalRow.created_at);
      }
    }
    return map;
  }, [allEvaluations]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader style={{ paddingBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UserGroupIcon style={{ width: '18px', height: '18px', color: 'var(--color-accent-blue)' }} />
            <CardTitle>My Channels</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Spinner size="sm" label="Loading channels..." />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader style={{ paddingBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserGroupIcon style={{ width: '18px', height: '18px', color: 'var(--color-accent-blue)' }} />
          <CardTitle>My Channels</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {channels.length === 0 ? (
          <div style={{ padding: '8px 0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
              You are not a member of any channels yet. Ask your coach for an invite.
            </p>
            <Link
              to="/channels"
              style={{
                fontSize: '13px',
                color: 'var(--color-accent-blue)',
                textDecoration: 'none',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
              }}
            >
              Browse public channels
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {channels.map((ch) => (
              <Link
                key={ch.id}
                to={`/channels/${ch.slug}`}
                style={{ textDecoration: 'none' }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 14px',
                    backgroundColor: 'var(--color-dark-elevated-1)',
                    border: '1px solid var(--color-border-subtle)',
                    borderRadius: '10px',
                    transition: 'border-color 140ms ease, background-color 140ms ease',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(61, 169, 252, 0.35)';
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(61, 169, 252, 0.04)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border-subtle)';
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-dark-elevated-1)';
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 600,
                        fontSize: '13px',
                        color: 'var(--color-text-primary)',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ch.display_name}
                    </p>
                    <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', margin: '2px 0 0' }}>
                      {ch.member_count} member{ch.member_count !== 1 ? 's' : ''}
                      {ch.sport_focus ? ` · ${ch.sport_focus}` : ''}
                      {(() => {
                        const lastEval = lastActivityByChannel.get(ch.id);
                        return lastEval ? ` · last eval ${formatRelative(lastEval)}` : '';
                      })()}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '12px' }}>
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: '10px',
                        color: 'var(--color-text-muted)',
                        backgroundColor: ch.listed
                          ? 'rgba(30, 215, 96, 0.10)'
                          : 'rgba(244, 201, 93, 0.10)',
                        border: `1px solid ${
                          ch.listed
                            ? 'rgba(30, 215, 96, 0.22)'
                            : 'rgba(244, 201, 93, 0.22)'
                        }`,
                        borderRadius: '999px',
                        padding: '2px 8px',
                      }}
                    >
                      {ch.listed ? 'Listed' : 'Unlisted'}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── LeaderboardOptInCard (B-2026-0072) ──────────────────────────────────────
// Shows leaderboard-enabled channels with an opt-in toggle per channel.
// OFF by default; athlete must explicitly opt in (privacy-first).

interface ChannelOptInState {
  slug: string;
  displayName: string;
  optIn: boolean;
  isToggling: boolean;
}

function LeaderboardOptInCard() {
  const { data: channels = [], isLoading } = useMyChannels();
  const { user } = useAuth();

  // Filter to channels where leaderboard is enabled
  const lbChannels = channels.filter((ch) => ch.leaderboard_enabled);

  const [optInStates, setOptInStates] = useState<Map<string, ChannelOptInState>>(new Map());
  const [isFetchingMemberships, setIsFetchingMemberships] = useState(false);

  useEffect(() => {
    if (!user || lbChannels.length === 0) return;
    setIsFetchingMemberships(true);
    // Fetch all members for leaderboard-enabled channels in parallel
    Promise.allSettled(
      lbChannels.map((ch) => getChannelMembers(ch.slug))
    ).then((results) => {
      const newMap = new Map<string, ChannelOptInState>();
      results.forEach((result, idx) => {
        const ch = lbChannels[idx];
        if (!ch) return;
        let optIn = false;
        if (result.status === 'fulfilled') {
          const myEntry = result.value.find((m) => m.user_id === user.id);
          optIn = myEntry?.leaderboard_opt_in ?? false;
        }
        newMap.set(ch.slug, {
          slug: ch.slug,
          displayName: ch.display_name,
          optIn,
          isToggling: false,
        });
      });
      setOptInStates(newMap);
    }).finally(() => setIsFetchingMemberships(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, lbChannels.length]);

  const handleToggle = async (slug: string) => {
    if (!user) return;
    const state = optInStates.get(slug);
    if (!state || state.isToggling) return;

    setOptInStates((prev) => {
      const next = new Map(prev);
      const entry = next.get(slug);
      if (entry) next.set(slug, { ...entry, isToggling: true });
      return next;
    });

    try {
      const result = await toggleLeaderboardOptIn(slug, user.id);
      setOptInStates((prev) => {
        const next = new Map(prev);
        const entry = next.get(slug);
        if (entry) next.set(slug, { ...entry, optIn: result.leaderboard_opt_in, isToggling: false });
        return next;
      });
    } catch {
      setOptInStates((prev) => {
        const next = new Map(prev);
        const entry = next.get(slug);
        if (entry) next.set(slug, { ...entry, isToggling: false });
        return next;
      });
    }
  };

  if (isLoading || isFetchingMemberships || lbChannels.length === 0) return null;

  return (
    <Card>
      <CardHeader style={{ paddingBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>🏆</span>
          <CardTitle>Leaderboard Visibility</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <p
          style={{
            fontSize: '13px',
            color: 'var(--color-text-muted)',
            marginBottom: '12px',
            lineHeight: 1.5,
          }}
        >
          Choose which channels can show your scores on their leaderboard.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {Array.from(optInStates.values()).map((state) => (
            <div
              key={state.slug}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                backgroundColor: 'var(--color-dark-elevated-1)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '10px',
                gap: '12px',
              }}
            >
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                  }}
                >
                  {state.displayName}
                </p>
                <p
                  style={{
                    margin: '2px 0 0',
                    fontSize: '11px',
                    color: state.optIn ? 'var(--color-accent)' : 'var(--color-text-muted)',
                  }}
                >
                  {state.optIn ? 'Visible on leaderboard' : 'Hidden from leaderboard'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleToggle(state.slug)}
                disabled={state.isToggling}
                aria-pressed={state.optIn}
                title={state.optIn ? 'Hide from leaderboard' : 'Show me on the leaderboard'}
                style={{
                  flexShrink: 0,
                  width: '40px',
                  height: '22px',
                  borderRadius: '999px',
                  border: 'none',
                  cursor: state.isToggling ? 'not-allowed' : 'pointer',
                  backgroundColor: state.optIn ? 'var(--color-accent)' : 'var(--color-border-strong)',
                  position: 'relative',
                  transition: 'background-color 200ms ease',
                  opacity: state.isToggling ? 0.6 : 1,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '2px',
                    left: state.optIn ? '20px' : '2px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    transition: 'left 200ms ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }}
                />
                <span className="sr-only">
                  {state.optIn ? 'Hide from leaderboard' : 'Show on leaderboard'}
                </span>
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── SchoolFeedCard (B-2026-0074) ────────────────────────────────────────────
// Shows the 3 most recent posts across all joined channels.
// SHARED-FILE NOTE: This component is owned by B-2026-0074.
// Do NOT edit the onboarding-checklist widget area (owned by B-2026-0078).

function formatFeedRelativeTime(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return diffMin <= 1 ? 'just now' : `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function FeedPostTypeIcon({ postType }: { postType: string }) {
  const style: React.CSSProperties = { width: '13px', height: '13px', flexShrink: 0 };
  if (postType === 'announcement') return <MegaphoneIcon style={{ ...style, color: 'var(--color-accent)' }} />;
  if (postType === 'milestone') return <SparklesIcon style={{ ...style, color: '#F5C542' }} />;
  return <ArrowPathIcon style={{ ...style, color: 'var(--color-accent-blue)' }} />;
}

function SchoolFeedCard() {
  const [posts, setPosts] = useState<ChannelFeedPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMyChannelFeed({ limit: 3 })
      .then((data) => { if (!cancelled) setPosts(data); })
      .catch(() => { /* silent — feed is non-critical */ })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Don't render the card at all if the user has no channel posts (empty feed = no channels or no posts)
  if (!isLoading && posts.length === 0) return null;

  return (
    <Card>
      <CardHeader style={{ paddingBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MegaphoneIcon style={{ width: '18px', height: '18px', color: 'var(--color-accent)' }} />
          <CardTitle>School Feed</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Spinner size="sm" label="Loading feed…" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/channels/${post.channel_slug}?tab=posts`}
                style={{ textDecoration: 'none' }}
              >
                <div
                  style={{
                    padding: '12px 14px',
                    backgroundColor: 'var(--color-dark-elevated-1)',
                    border: '1px solid var(--color-border-subtle)',
                    borderRadius: '10px',
                    transition: 'border-color 140ms ease, background-color 140ms ease',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(30,215,96,0.30)';
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'rgba(30,215,96,0.03)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border-subtle)';
                    (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-dark-elevated-1)';
                  }}
                >
                  {/* Channel name + time */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                      <FeedPostTypeIcon postType={post.post_type} />
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'var(--color-text-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {post.channel_name}
                      </span>
                    </div>
                    <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                      {formatFeedRelativeTime(post.created_at)}
                    </span>
                  </div>
                  {/* Post body preview */}
                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-secondary)',
                      lineHeight: 1.55,
                      margin: 0,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {post.body}
                  </p>
                  {/* Author */}
                  {post.author_display_name && (
                    <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', margin: '6px 0 0' }}>
                      By {post.author_display_name}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── PublishConsentPromptCard (Goal Score go-live B1) ───────────────────────
// Lists coach-submitted evaluations awaiting this athlete's Yes/No on
// leaderboard publishing. Stays visible until answered — once this athlete
// answers (Yes or No), it is removed from the local dismissed set so it
// disappears immediately; on the next real page load the backend's
// publish_state is no longer 'awaiting_athlete' so it would not re-appear
// even without the local state.

interface PublishConsentPromptItem {
  evaluationId: string;
  templateName: string | null;
  coachDisplayName: string | null;
}

function PublishConsentPromptCard() {
  // Goal Score go-live B5a (red-team finding F4): this must NOT depend on
  // the main dashboard list's default page_size=20 — a coach-submitted
  // evaluation awaiting the athlete's Yes/No can be older than the newest
  // 20 self-evals and would otherwise never surface. A dedicated,
  // server-side-filtered query (own cache key, own request) finds EVERY
  // awaiting_athlete row regardless of recency.
  const { data: pendingEvaluations = [] } = useMyEvaluations({
    publish_state: 'awaiting_athlete',
    page_size: 100,
  });
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [errorByEvalId, setErrorByEvalId] = useState<Map<string, string>>(new Map());

  const pending: PublishConsentPromptItem[] = useMemo(
    () =>
      pendingEvaluations
        // Defensive — the server query already filters to
        // publish_state=='awaiting_athlete', but this never trusts that
        // alone (e.g. a stale cached response from a differently-filtered
        // query under the same hook).
        //
        // Goal E (E4): a coach/admin who views this page as a member of a
        // channel can be the CREATOR of a coach-submitted evaluation they
        // are not the subject of. This athlete-facing consent prompt is
        // "did your coach submit this FOR you" — exclude any row this
        // viewer created themselves so a coach never sees their own prompt.
        .filter(
          (e) =>
            e.publish_state === 'awaiting_athlete' &&
            !dismissed.has(e.evaluation_id) &&
            e.created_by_user_id !== user?.id
        )
        .map((e) => ({
          evaluationId: e.evaluation_id,
          templateName: e.template_name,
          coachDisplayName: e.created_by_display_name ?? null,
        })),
    [pendingEvaluations, dismissed, user?.id]
  );

  if (pending.length === 0) return null;

  const handleAnswer = async (evaluationId: string, publish: boolean) => {
    setAnsweringId(evaluationId);
    setErrorByEvalId((prev) => {
      const next = new Map(prev);
      next.delete(evaluationId);
      return next;
    });
    try {
      await setPublishConsent(evaluationId, publish);
      setDismissed((prev) => new Set(prev).add(evaluationId));
    } catch (err) {
      setErrorByEvalId((prev) => new Map(prev).set(evaluationId, extractPublishConsentErrorMessage(err)));
    } finally {
      setAnsweringId(null);
    }
  };

  return (
    <div className="sf-s2" style={{ marginBottom: '20px' }}>
    <Card data-testid="publish-consent-prompt-card">
      <CardHeader style={{ paddingBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>🏆</span>
          <CardTitle>Your coach shared a score with you</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {pending.map((item) => (
            <div
              key={item.evaluationId}
              data-testid="publish-consent-prompt-item"
              style={{
                padding: '12px 14px',
                backgroundColor: 'var(--color-dark-elevated-1)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '10px',
              }}
            >
              <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--color-text-primary)' }}>
                {(item.coachDisplayName ?? 'Your coach')}
                {item.templateName ? ` shared your "${item.templateName}" score with you.` : ' shared a score with you.'}
              </p>
              <p style={{ margin: '0 0 10px', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                Do you want to publish it on the leaderboard? Yes or no is up to you.
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  type="button"
                  data-testid="publish-consent-prompt-yes"
                  onClick={() => void handleAnswer(item.evaluationId, true)}
                  disabled={answeringId === item.evaluationId}
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--color-text-inverse)',
                    background: 'var(--color-accent)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    cursor: answeringId === item.evaluationId ? 'not-allowed' : 'pointer',
                    opacity: answeringId === item.evaluationId ? 0.6 : 1,
                  }}
                >
                  Yes
                </button>
                <button
                  type="button"
                  data-testid="publish-consent-prompt-no"
                  onClick={() => void handleAnswer(item.evaluationId, false)}
                  disabled={answeringId === item.evaluationId}
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    background: 'transparent',
                    border: '1px solid var(--color-border-subtle)',
                    borderRadius: '6px',
                    padding: '6px 14px',
                    cursor: answeringId === item.evaluationId ? 'not-allowed' : 'pointer',
                    opacity: answeringId === item.evaluationId ? 0.6 : 1,
                  }}
                >
                  No
                </button>
                <Link
                  to={`/evaluations/${item.evaluationId}`}
                  style={{ fontSize: '11px', color: 'var(--color-accent-blue)', marginLeft: 'auto' }}
                >
                  View evaluation
                </Link>
              </div>
              {errorByEvalId.get(item.evaluationId) && (
                <p
                  data-testid="publish-consent-prompt-error"
                  style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--color-accent-red)' }}
                >
                  {errorByEvalId.get(item.evaluationId)}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// Number of recent evaluations to hydrate with full detail on dashboard mount.
// Set to 8 to match the PhaseHeatmap window (evaluations.slice(-8)).
const HYDRATION_LIMIT = 8;

export default function AthleteDashboard() {
  const { user } = useAuth();
  const { data: allEvaluations = [], isLoading } = useMyEvaluations();
  // B-2026-0078: onboarding checklist state
  const { data: onboardingStatus } = useOnboardingStatus();
  // B-2026-0075: milestone badges
  const { data: milestonesData } = useMyMilestones();
  const { data: myChannels = [] } = useMyChannels();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | 'all'>('all');

  // ── Tier 2 hydration ───────────────────────────────────────────────────────
  // The summary list (/users/me/evaluations) omits region_scores, phase_scores,
  // and llm_explanation to keep the payload small. We fetch full details for the
  // most recent HYDRATION_LIMIT evals in parallel so Phase-Heatmap, Traffic-Light
  // body regions, Coaching History, and "What to Work On" have data to render.
  const recentIds = useMemo(() => {
    const byDate = [...allEvaluations].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return byDate.slice(0, HYDRATION_LIMIT).map((e) => e.evaluation_id);
  }, [allEvaluations]);

  const detailQueries = useEvaluationDetails(recentIds);

  // Merge hydrated detail fields back into the summary items.
  // Summary fields (overall_score, form_score, etc.) are authoritative and kept as-is.
  // Only region_scores, phase_scores, llm_explanation are overlaid from the full detail.
  const hydratedEvaluations = useMemo(() => {
    const detailMap = new Map<
      string,
      { region_scores?: RegionScore[]; phase_scores?: PhaseScore[]; llm_explanation?: LLMExplanation | null }
    >();
    detailQueries.forEach((q, i) => {
      const id = recentIds[i];
      if (q.data && id) {
        detailMap.set(id, {
          region_scores: q.data.region_scores,
          phase_scores: q.data.phase_scores,
          llm_explanation: q.data.llm_explanation,
        });
      }
    });
    return allEvaluations.map((e) => {
      const detail = detailMap.get(e.evaluation_id);
      if (!detail) return e;
      return {
        ...e,
        region_scores: detail.region_scores ?? e.region_scores,
        phase_scores: detail.phase_scores ?? e.phase_scores,
        llm_explanation: detail.llm_explanation ?? e.llm_explanation,
      };
    });
  }, [allEvaluations, detailQueries, recentIds]);

  // Whether any detail fetch is still in flight (used for subtle loading indicators)
  const isHydrating = detailQueries.some((q) => q.isFetching);

  // Build template options from the user's own evaluations
  const templateOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const e of allEvaluations) {
      if (e.template_id && !seen.has(e.template_id)) {
        seen.set(e.template_id, e.template_name ?? e.template_id.slice(0, 8));
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [allEvaluations]);

  // Default to most recently used template when evals exist
  const defaultTemplateId = useMemo(() => {
    if (allEvaluations.length === 0) return 'all';
    const sorted = [...allEvaluations].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return sorted[0]?.template_id ?? 'all';
  }, [allEvaluations]);

  // Apply default on first data load (only once)
  const [hasAppliedDefault, setHasAppliedDefault] = useState(false);
  useMemo(() => {
    if (!isLoading && !hasAppliedDefault && allEvaluations.length > 0) {
      setSelectedTemplateId(defaultTemplateId);
      setHasAppliedDefault(true);
    }
  }, [isLoading, hasAppliedDefault, allEvaluations.length, defaultTemplateId]);

  // Client-side filter by selected template.
  // Uses hydratedEvaluations so that region_scores, phase_scores, and llm_explanation
  // are available for the Tier 2 feature cards (Traffic Light, Heatmap, Coaching History,
  // "What to Work On"). The summary fields (scores, dates) come from allEvaluations and
  // are preserved verbatim — only the missing detail fields are overlaid.
  const filteredEvaluations = useMemo(() => {
    if (selectedTemplateId === 'all') return hydratedEvaluations;
    return hydratedEvaluations.filter((e) => e.template_id === selectedTemplateId);
  }, [hydratedEvaluations, selectedTemplateId]);

  // Sort evaluations chronologically
  const sorted = useMemo(
    () =>
      [...filteredEvaluations].sort(
        (a, b) =>
          new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime()
      ),
    [filteredEvaluations]
  );

  // ── Derived analytics ──────────────────────────────────────────────────────
  const latest = sorted[sorted.length - 1];
  const latestScore = Math.round(latest?.overall_score ?? 0);

  const personalBest = useMemo(
    () => Math.round(Math.max(0, ...sorted.map((e) => e.overall_score ?? 0))),
    [sorted]
  );
  const pbIndex = useMemo(
    () =>
      sorted.findIndex((e) => Math.round(e.overall_score ?? 0) === personalBest),
    [sorted, personalBest]
  );
  const pbEval = sorted[pbIndex] ?? null;
  const isPB =
    sorted.length > 0 &&
    latest != null &&
    Math.round(latest.overall_score ?? 0) === personalBest &&
    personalBest > 0;
  const deltaPB = sorted.length > 0 ? latestScore - personalBest : 0;

  const velocity = useMemo(
    () => (sorted.length > 0 ? velocityLabel(sorted) : null),
    [sorted]
  );

  // Region buckets from latest evaluation
  const regions: RegionScore[] = useMemo(
    () => latest?.region_scores ?? [],
    [latest]
  );
  const improving = regions.filter((r) => r.score >= 80);
  const plateaued = regions.filter((r) => r.score >= 60 && r.score < 80);
  const needsWork = regions.filter((r) => r.score < 60);

  // Chart data — use direct form_score/timing_score from EvaluationSummaryItem
  const chartData = useMemo(
    () =>
      sorted.map((e) => {
        // Phase-level aggregation as fallback for form/timing
        const phases = e.phase_scores ?? [];
        const phaseFormScore =
          phases.length > 0
            ? phases.reduce(
                (sum: number, p: PhaseScore) =>
                  sum + (p.form_score ?? p.score ?? p.overall_phase_score ?? 0),
                0
              ) / phases.length
            : undefined;
        const phaseTimingScore =
          phases.length > 0
            ? phases.reduce((sum: number, p: PhaseScore) => sum + (p.timing_score ?? 0), 0) /
              phases.length
            : undefined;

        // Prefer top-level form_score/timing_score from the summary item
        const formScore = e.form_score ?? phaseFormScore;
        const timingScore = e.timing_score ?? phaseTimingScore;

        return {
          date: formatDate(e.created_at, 'MMM d'),
          score: Math.round(e.overall_score ?? 0),
          form: formScore != null ? Math.round(formScore) : undefined,
          timing:
            timingScore != null && timingScore > 0
              ? Math.round(timingScore)
              : undefined,
          isPB: Math.round(e.overall_score ?? 0) === personalBest,
          // Goal Score follow-ups batch 1: `total_score` is `null`/absent on
          // every legacy row and whenever the Goal Score switches are off
          // (today's prod default) — undefined here means "no Total series
          // point," never a plotted 0 (LFT-063 sentinel convention).
          total: e.total_score != null ? Math.round(e.total_score * 10) / 10 : undefined,
        };
      }),
    [sorted, personalBest]
  );

  const hasFormData = chartData.some((d) => d.form !== undefined);
  const hasTimingData = chartData.some(
    (d) => d.timing !== undefined && (d.timing ?? 0) > 0
  );
  // Goal Score follow-ups batch 1: Total is bonus x effect and has no
  // ceiling (REPORT.md §2) — only show the series when at least one
  // evaluation actually carries a non-null total_score.
  const hasTotalData = chartData.some((d) => d.total !== undefined);

  // "What to work on" — worst trending region/phase
  const recommendation = useMemo(() => {
    if (sorted.length < 2) return null;

    if (needsWork.length > 0) {
      const worst = [...needsWork].sort((a, b) => a.score - b.score)[0];
      const persistedFor = sorted
        .slice(-3)
        .filter((e) => {
          const r = e.region_scores?.find(
            (rs) => rs.region_id === worst.region_id
          );
          return r && r.score < 60;
        }).length;
      if (persistedFor >= 2) {
        return `Focus on "${worst.region_name}" — it has scored below 60 for ${persistedFor} sessions in a row.`;
      }
    }

    // Fall back to worst phase
    const latestPhases = latest?.phase_scores ?? [];
    if (latestPhases.length > 0) {
      const sortedPhases = [...latestPhases].sort(
        (a, b) =>
          (a.form_score ?? a.score ?? 100) - (b.form_score ?? b.score ?? 100)
      );
      const worst = sortedPhases[0];
      const name =
        worst.phase_name ?? worst.phase_label ?? 'a phase';
      const score =
        worst.form_score ??
        worst.score ??
        worst.overall_phase_score ??
        0;
      if (score < 70) {
        return `Your "${name}" phase needs attention — current form score is ${Math.round(
          score
        )}.`;
      }
    }

    return null;
  }, [sorted, needsWork, latest]);

  // Coaching history — last 3 evaluations with LLM feedback
  const coachingHistory = useMemo(
    () =>
      sorted
        .filter((e) => e.llm_explanation)
        .slice(-3)
        .reverse(),
    [sorted]
  );

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '60vh',
        }}
      >
        <Spinner size="xl" label="Loading your progress..." />
      </div>
    );
  }

  const displayName = user?.display_name ?? user?.email ?? 'Athlete';
  const hasData = sorted.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes sf-pulse-fade-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .sf-athlete-page  { animation: sf-pulse-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .sf-s1 { animation: sf-pulse-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.04s both; }
        .sf-s2 { animation: sf-pulse-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both; }
        .sf-s3 { animation: sf-pulse-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.20s both; }
        .sf-s4 { animation: sf-pulse-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.28s both; }
        .sf-s5 { animation: sf-pulse-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.36s both; }
        .sf-s6 { animation: sf-pulse-fade-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.44s both; }
      `}</style>

      <div
        className="sf-athlete-page"
        style={{ maxWidth: '900px', margin: '0 auto', paddingBottom: '48px' }}
      >
        {/* ── Page greeting ──────────────────────────────────────────────── */}
        <div
          className="sf-s1"
          style={{
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <div>
            <p
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: '13px',
                color: 'var(--color-text-muted)',
                marginBottom: '4px',
                letterSpacing: '0.2px',
              }}
            >
              Welcome back,
            </p>
            <h1
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '26px',
                color: 'var(--color-text-primary)',
                letterSpacing: '-0.3px',
                margin: 0,
              }}
            >
              {displayName}
            </h1>
          </div>
          <div style={{ paddingTop: '4px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* B-2026-0020: primary athlete action — one click to a new evaluation.
                Routes to /evaluate (the working submit-and-poll flow). */}
            <Link
              to="/evaluate"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-text-inverse)',
                background: 'var(--color-accent-blue)',
                borderRadius: '8px',
                padding: '8px 14px',
                boxShadow: '0 0 18px rgba(61,169,252,0.3)',
                textDecoration: 'none',
              }}
            >
              <SparklesIcon className="w-4 h-4" />
              Take new evaluation
            </Link>
          </div>
        </div>

        {/* ── Template Filter ─────────────────────────────────────────────── */}
        {templateOptions.length > 1 && (
          <div
            className="sf-s2"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px',
            }}
          >
            <label
              htmlFor="template-filter"
              style={{ fontSize: '12px', color: 'var(--color-text-muted)', flexShrink: 0 }}
            >
              Filter by template:
            </label>
            <select
              id="template-filter"
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value as string)}
              style={{
                fontSize: '13px',
                color: 'var(--color-text-primary)',
                backgroundColor: 'var(--color-dark-card)',
                border: '1px solid var(--color-border-subtle)',
                borderRadius: '8px',
                padding: '5px 10px',
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="all">All templates</option>
              {templateOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* ── Onboarding Checklist (B-2026-0078) ─────────────────────────── */}
        {/* OWN REGION: OnboardingChecklist widget only — do NOT add to this block */}
        {onboardingStatus && !onboardingStatus.onboarding_complete && (
          <div className="sf-s2">
            <OnboardingChecklist
              status={onboardingStatus}
              channels={myChannels}
            />
          </div>
        )}

        {/* ── Publish-consent prompt (Goal Score go-live B1) ─────────────── */}
        {/* Stays visible until the athlete answers every pending coach-
            submitted evaluation's Yes/No; renders nothing otherwise (the
            component itself owns its own margin so an empty render leaves
            no stray gap). */}
        <PublishConsentPromptCard />

        {/* ── Hero Score Section ──────────────────────────────────────────── */}
        <div
          className="sf-s2"
          style={{
            position: 'relative',
            backgroundColor: 'var(--color-dark-card)',
            border: '1px solid var(--color-border-subtle)',
            borderRadius: '20px',
            padding: '32px 28px',
            marginBottom: '20px',
            boxShadow: 'var(--shadow-card)',
            overflow: 'hidden',
          }}
        >
          {/* Atmospheric glow behind ring */}
          {hasData && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '50%',
                left: '8%',
                transform: 'translate(-50%, -50%)',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${scoreColor(latestScore)}1A 0%, transparent 68%)`,
                pointerEvents: 'none',
              }}
            />
          )}

          {hasData ? (
            <div
              style={{
                position: 'relative',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '28px',
              }}
            >
              {/* Score ring */}
              <div style={{ flexShrink: 0 }}>
                <ScoreRing score={latestScore} variant="hero" />
              </div>

              {/* Text stack */}
              <div style={{ flex: '1 1 200px' }}>
                <p
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '12px',
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                    marginBottom: '6px',
                  }}
                >
                  Latest Session
                </p>
                <h2
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    fontSize: '48px',
                    color: 'var(--color-text-primary)',
                    lineHeight: 1,
                    margin: '0 0 10px',
                    letterSpacing: '-2px',
                  }}
                >
                  {latestScore}
                  <span
                    style={{
                      fontSize: '18px',
                      fontWeight: 400,
                      color: 'var(--color-text-muted)',
                      letterSpacing: 0,
                    }}
                  >
                    {' '}
                    / 100
                  </span>
                </h2>
                <p
                  style={{
                    fontSize: '12px',
                    color: 'var(--color-text-muted)',
                    marginBottom: '6px',
                    fontWeight: 500,
                  }}
                >
                  {scoreLabel(latestScore)}
                </p>

                {/* PB badge */}
                {isPB ? (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      backgroundColor: 'rgba(244, 201, 93, 0.12)',
                      border: '1px solid rgba(244, 201, 93, 0.30)',
                      borderRadius: '999px',
                      padding: '4px 12px',
                      marginBottom: '12px',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>🏆</span>
                    <span
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 700,
                        fontSize: '13px',
                        color: 'var(--color-accent-gold)',
                        textShadow: '0 0 12px rgba(244, 201, 93, 0.5)',
                      }}
                    >
                      New Personal Best!
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      backgroundColor:
                        deltaPB >= 0
                          ? 'rgba(30, 215, 96, 0.08)'
                          : 'rgba(255, 93, 93, 0.08)',
                      border: `1px solid ${
                        deltaPB >= 0
                          ? 'rgba(30, 215, 96, 0.22)'
                          : 'rgba(255, 93, 93, 0.22)'
                      }`,
                      borderRadius: '999px',
                      padding: '4px 12px',
                      marginBottom: '12px',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 600,
                        fontSize: '13px',
                        color:
                          deltaPB >= 0
                            ? 'var(--color-accent)'
                            : 'var(--color-accent-red)',
                      }}
                    >
                      {deltaPB >= 0 ? `+${deltaPB}` : deltaPB} from PB ({personalBest})
                    </span>
                  </div>
                )}

                {/* Velocity — extracted to VelocityDisplay (Feature 5) */}
                {velocity && <VelocityDisplay velocity={velocity} />}

                <p
                  style={{
                    fontSize: '11px',
                    color: 'var(--color-text-muted)',
                    marginTop: '8px',
                  }}
                >
                  {sorted.length} session{sorted.length !== 1 ? 's' : ''} recorded
                  &nbsp;·&nbsp; PB: {personalBest}
                </p>
              </div>
            </div>
          ) : (
            <EmptyState />
          )}
        </div>

        {/* ── Action Cards ────────────────────────────────────────────────── */}
        {hasData && (
          <div
            className="sf-s3"
            style={{
              display: 'flex',
              gap: '14px',
              flexWrap: 'wrap',
              marginBottom: '20px',
            }}
          >
            <ActionCard
              title="Improving"
              count={improving.length}
              subtitle="Regions scoring 80+"
              names={improving.map((r) => r.region_name)}
              accent="var(--color-accent)"
              icon={
                <ArrowTrendingUpIcon style={{ width: '18px', height: '18px' }} />
              }
            />
            <ActionCard
              title="Plateaued"
              count={plateaued.length}
              subtitle="Consistent but room to grow"
              names={plateaued.map((r) => r.region_name)}
              accent="var(--color-accent-gold)"
              icon={<MinusCircleIcon style={{ width: '18px', height: '18px' }} />}
            />
            <ActionCard
              title="Needs Work"
              count={needsWork.length}
              subtitle="Regions scoring below 60"
              names={needsWork.map((r) => r.region_name)}
              accent="var(--color-accent-red)"
              icon={
                <ExclamationTriangleIcon
                  style={{ width: '18px', height: '18px' }}
                />
              }
            />
          </div>
        )}

        {/* ── Score Timeline ──────────────────────────────────────────────── */}
        {sorted.length >= 2 && (
          <div className="sf-s4" style={{ marginBottom: '20px' }}>
            <Card>
              <CardHeader style={{ paddingBottom: '8px' }}>
                <CardTitle>Your Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="sf-score-grad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#1ED760"
                          stopOpacity={0.22}
                        />
                        <stop
                          offset="95%"
                          stopColor="#1ED760"
                          stopOpacity={0.0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--color-border-subtle)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      // Goal Score follow-ups batch 1: Total has no ceiling
                      // (REPORT.md §2), so when a Total series is present the
                      // axis must grow past 100 to fit it; otherwise keep the
                      // original fixed 0-100 domain (byte-identical today).
                      domain={hasTotalData ? [0, (dataMax: number) => Math.max(100, dataMax)] : [0, 100]}
                      tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartTooltip />} />
                    {hasTotalData && (
                      <Line
                        type="monotone"
                        dataKey="total"
                        name="Total"
                        stroke="#B57BFF"
                        strokeWidth={2}
                        dot={false}
                      />
                    )}
                    {hasFormData && (
                      <Line
                        type="monotone"
                        dataKey="form"
                        name="Form"
                        stroke="rgba(61, 169, 252, 0.6)"
                        strokeWidth={1.5}
                        dot={false}
                        strokeDasharray="4 3"
                      />
                    )}
                    {hasTimingData && (
                      <Line
                        type="monotone"
                        dataKey="timing"
                        name="Timing"
                        stroke="rgba(255, 138, 61, 0.6)"
                        strokeWidth={1.5}
                        dot={false}
                        strokeDasharray="4 3"
                      />
                    )}
                    <Area
                      type="monotone"
                      dataKey="score"
                      name="Overall"
                      stroke="#1ED760"
                      strokeWidth={2.5}
                      fill="url(#sf-score-grad)"
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      dot={(props: any) => {
                        const { cx, cy, payload, index } = props;
                        if (!cx || !cy) return <g key={`dot-empty-${index}`} />;
                        if (payload.isPB) {
                          return (
                            <circle
                              key={`dot-pb-${index}`}
                              cx={cx}
                              cy={cy}
                              r={6}
                              fill="#F4C95D"
                              stroke="#0B0F14"
                              strokeWidth={2}
                            />
                          );
                        }
                        return (
                          <circle
                            key={`dot-${index}`}
                            cx={cx}
                            cy={cy}
                            r={3}
                            fill="#1ED760"
                            stroke="#0B0F14"
                            strokeWidth={1.5}
                          />
                        );
                      }}
                    />
                    {/* PB label */}
                    {pbIndex >= 0 && chartData[pbIndex] && (
                      <ReferenceDot
                        x={chartData[pbIndex].date}
                        y={chartData[pbIndex].score}
                        r={0}
                        label={{
                          value: 'PB',
                          position: 'top',
                          fill: 'var(--color-accent-gold)',
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>

                {/* Chart legend */}
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginTop: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  {[
                    {
                      color: '#1ED760',
                      label: 'Overall Score',
                      dashed: false,
                      show: true,
                    },
                    {
                      color: '#B57BFF',
                      label: 'Total (Goals x Form)',
                      dashed: false,
                      show: hasTotalData,
                    },
                    {
                      color: 'rgba(61,169,252,0.8)',
                      label: 'Form Score',
                      dashed: true,
                      show: hasFormData,
                    },
                    {
                      color: 'rgba(255,138,61,0.8)',
                      label: 'Timing Score',
                      dashed: true,
                      show: hasTimingData,
                    },
                  ]
                    .filter((item) => item.show)
                    .map((item) => (
                      <div
                        key={item.label}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <svg width="20" height="8">
                          <line
                            x1="0"
                            y1="4"
                            x2="20"
                            y2="4"
                            stroke={item.color}
                            strokeWidth="2"
                            strokeDasharray={item.dashed ? '4 3' : undefined}
                          />
                        </svg>
                        <span
                          style={{
                            fontSize: '11px',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          {item.label}
                        </span>
                      </div>
                    ))}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <svg width="14" height="14">
                      <circle cx="7" cy="7" r="5" fill="#F4C95D" />
                    </svg>
                    <span
                      style={{
                        fontSize: '11px',
                        color: 'var(--color-text-muted)',
                      }}
                    >
                      Personal Best
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Recurring Issues ─────────────────────────────────────────────── */}
        {hasData && (
          <div className="sf-s4" style={{ marginBottom: '20px' }}>
            <RecurringIssuesCard evaluations={[...sorted].reverse()} />
          </div>
        )}

        {/* ── Phase Heatmap ───────────────────────────────────────────────── */}
        {hasData && (
          <div className="sf-s4" style={{ marginBottom: '20px' }}>
            <Card>
              <CardHeader style={{ paddingBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CardTitle>Phase Breakdown</CardTitle>
                  {isHydrating && (
                    <div style={{ opacity: 0.5 }}>
                      <Spinner size="sm" label="Loading phase detail..." />
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {sorted.some(
                  (e) => e.phase_scores && e.phase_scores.length > 0
                ) ? (
                  <>
                    <p
                      style={{
                        fontSize: '12px',
                        color: 'var(--color-text-muted)',
                        marginBottom: '16px',
                      }}
                    >
                      Phase form scores across your last{' '}
                      {Math.min(sorted.length, 8)} sessions. Darker cells =
                      stronger performance.
                    </p>
                    <PhaseHeatmap evaluations={sorted} />
                    {/* Color legend */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '14px',
                        marginTop: '14px',
                        flexWrap: 'wrap',
                      }}
                    >
                      {[
                        {
                          bg: 'rgba(30,215,96,0.22)',
                          border: 'rgba(30,215,96,0.35)',
                          label: '80+',
                        },
                        {
                          bg: 'rgba(244,201,93,0.22)',
                          border: 'rgba(244,201,93,0.35)',
                          label: '60–79',
                        },
                        {
                          bg: 'rgba(255,93,93,0.22)',
                          border: 'rgba(255,93,93,0.35)',
                          label: '<60',
                        },
                        {
                          bg: 'var(--color-dark-elevated-2)',
                          border: 'var(--color-border-subtle)',
                          label: 'No data',
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          <div
                            style={{
                              width: '14px',
                              height: '14px',
                              borderRadius: '3px',
                              backgroundColor: item.bg,
                              border: `1px solid ${item.border}`,
                            }}
                          />
                          <span
                            style={{
                              fontSize: '11px',
                              color: 'var(--color-text-muted)',
                            }}
                          >
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p
                    style={{
                      fontSize: '13px',
                      color: 'var(--color-text-muted)',
                      padding: '8px 0',
                    }}
                  >
                    Phase data will appear after evaluations complete with phase
                    analysis.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── AI Coaching History ──────────────────────────────────────────── */}
        {coachingHistory.length > 0 && (
          <div className="sf-s5" style={{ marginBottom: '20px' }}>
            <Card>
              <CardHeader style={{ paddingBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <SparklesIcon
                    style={{
                      width: '18px',
                      height: '18px',
                      color: 'var(--color-accent-blue)',
                    }}
                  />
                  <CardTitle>AI Coaching History</CardTitle>
                  {isHydrating && (
                    <div style={{ opacity: 0.5 }}>
                      <Spinner size="sm" label="Loading coaching detail..." />
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {coachingHistory.map((e, i) => (
                    <CoachingEntry
                      key={e.evaluation_id}
                      date={e.created_at}
                      score={e.overall_score ?? 0}
                      summary={e.llm_explanation?.overall_summary}
                      tips={e.llm_explanation?.improvement_tips}
                      defaultOpen={i === 0}
                      createdByDisplayName={e.created_by_display_name}
                      sharedWithDisplayName={e.shared_with_display_name}
                      totalScore={e.total_score}
                      goalBonusScore={e.goal_bonus_score}
                      publishState={e.publish_state}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Best vs Latest Auto-Compare ─────────────────────────────────── */}
        {sorted.length >= 2 && pbEval && latest && (
          <div className="sf-s5" style={{ marginBottom: '20px' }}>
            <PBDeltaCard
              pbEval={pbEval}
              latestEval={latest}
              isLatestThePB={isPB}
            />
          </div>
        )}

        {/* ── My Channels ──────────────────────────────────────────────────── */}
        <div className="sf-s6" style={{ marginBottom: '20px' }}>
          <MyChannelsCard />
        </div>

        {/* ── Leaderboard Opt-In (B-2026-0072) ─────────────────────────────── */}
        <div className="sf-s6" style={{ marginBottom: '20px' }}>
          <LeaderboardOptInCard />
        </div>

        {/* ── School Feed (B-2026-0074) ────────────────────────────────────── */}
        {/* SHARED-FILE REGION: owned by B-2026-0074. Do NOT touch onboarding-checklist widget area (B-2026-0078). */}
        <div className="sf-s6" style={{ marginBottom: '20px' }}>
          <SchoolFeedCard />
        </div>

        {/* ── Recent Coach Comments (B-2026-0020) ──────────────────────────── */}
        <div className="sf-s6" style={{ marginBottom: '20px' }}>
          <RecentCoachCommentsCard />
        </div>

        {/* ── Badges (B-2026-0075) ─────────────────────────────────────────── */}
        {milestonesData && milestonesData.milestones.length > 0 && (
          <div
            className="sf-s6"
            style={{
              marginBottom: '20px',
              backgroundColor: 'var(--color-dark-card)',
              border: '1px solid var(--color-border-subtle)',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            <MilestoneGrid
              milestones={milestonesData.milestones}
              currentStreakWeeks={milestonesData.current_streak_weeks}
              showStreak
            />
          </div>
        )}

        {/* ── "What to Work On" Banner ─────────────────────────────────────── */}
        {recommendation && (
          <div
            className="sf-s6"
            style={{
              display: 'flex',
              gap: '14px',
              alignItems: 'flex-start',
              backgroundColor: 'rgba(61, 169, 252, 0.07)',
              border: '1px solid rgba(61, 169, 252, 0.20)',
              borderRadius: '14px',
              padding: '16px 20px',
            }}
          >
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                backgroundColor: 'rgba(61, 169, 252, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LightBulbIcon
                style={{
                  width: '18px',
                  height: '18px',
                  color: 'var(--color-accent-blue)',
                }}
              />
            </div>
            <div>
              <p
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--color-accent-blue)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  marginBottom: '4px',
                }}
              >
                Recommended Focus
              </p>
              <p
                style={{
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.5,
                }}
              >
                {recommendation}
              </p>
            </div>
          </div>
        )}

        {/* ── No-data fallback hint ─────────────────────────────────────────── */}
        {!hasData && (
          <div
            className="sf-s3"
            style={{
              textAlign: 'center',
              padding: '16px 24px',
              fontSize: '13px',
              color: 'var(--color-text-muted)',
            }}
          >
            Ask your coach to run an evaluation, or upload a video to get started.
          </div>
        )}
      </div>
    </>
  );
}
